import type { HttpClient } from "../http/client";
import type { Lang } from "../wire/attendee";
import type {
  AttendeeCorrection,
  AttendeeRecord,
  Campaign,
  CampaignAction,
  CampaignCreateRequest,
  EventPatchRequest,
  Hold,
  ManagedAsset,
  ManagedEvent,
  ManagedSession,
  PasswordResponse,
  SessionWriteRequest,
  StaffCreateRequest,
  StaffCreateResponse,
  StaffMember,
  StaffPatchRequest,
  TaxonomiesResponse,
  TaxonomyItem,
  TaxonomyKind,
  TaxonomyPatchRequest,
  TemplateDetail,
  TemplateKey,
  TemplateSaveRequest,
  TemplateSummary,
} from "../wire/superuser";
import type { ProfileInput } from "../wire/attendee";

const id = (v: string) => encodeURIComponent(v);

function fileForm(file: Blob): FormData {
  const form = new FormData();
  form.append("file", file);
  return form;
}

/** The superuser's writing pages (docs/api.md 45). Every route is superuser-only. */
export function createSuperuserApi(http: HttpClient) {
  return {
    /* 45.2 Staff */
    staff: {
      list: () => http.json<{ staff: StaffMember[] }>("GET", "/superuser/staff"),
      create: (body: StaffCreateRequest) => http.json<StaffCreateResponse>("POST", "/superuser/staff", { body }),
      update: (staffId: string, body: StaffPatchRequest) =>
        http.json<StaffMember>("PATCH", `/superuser/staff/${id(staffId)}`, { body }),
      resetPassword: (staffId: string, password?: string) =>
        http.json<PasswordResponse>("POST", `/superuser/staff/${id(staffId)}/password`, {
          body: password ? { password } : {},
        }),
      revoke: (staffId: string) => http.void("POST", `/superuser/staff/${id(staffId)}/revoke`),
    },

    /* 45.3 Event */
    event: {
      get: () => http.json<ManagedEvent>("GET", "/superuser/event"),
      update: (body: EventPatchRequest) => http.json<ManagedEvent>("PATCH", "/superuser/event", { body }),
    },

    /* 45.4 Sessions */
    sessions: {
      list: () => http.json<{ sessions: ManagedSession[] }>("GET", "/superuser/sessions"),
      create: (body: SessionWriteRequest) => http.json<ManagedSession>("POST", "/superuser/sessions", { body }),
      update: (sessionId: string, body: SessionWriteRequest) =>
        http.json<ManagedSession>("PATCH", `/superuser/sessions/${id(sessionId)}`, { body }),
      setPoster: (sessionId: string, file: Blob) =>
        http.json<ManagedAsset>("PUT", `/superuser/sessions/${id(sessionId)}/poster`, { form: fileForm(file) }),
      removePoster: (sessionId: string) => http.void("DELETE", `/superuser/sessions/${id(sessionId)}/poster`),
    },

    /* 45.5 Assets */
    assets: {
      list: () => http.json<{ assets: ManagedAsset[] }>("GET", "/superuser/assets"),
      put: (key: string, file: Blob) =>
        http.json<ManagedAsset>("PUT", `/superuser/assets/${id(key)}`, { form: fileForm(file) }),
      remove: (key: string) => http.void("DELETE", `/superuser/assets/${id(key)}`),
    },

    /* 45.6 Taxonomies */
    taxonomies: {
      list: () => http.json<TaxonomiesResponse>("GET", "/superuser/taxonomies"),
      add: (kind: TaxonomyKind, label: string) =>
        http.json<TaxonomyItem>("POST", `/superuser/taxonomies/${kind}`, { body: { label } }),
      update: (kind: TaxonomyKind, itemId: string, body: TaxonomyPatchRequest) =>
        http.json<TaxonomyItem>("PATCH", `/superuser/taxonomies/${kind}/${id(itemId)}`, { body }),
    },

    /* 45.7 Templates and holds */
    templates: {
      list: (lang: Lang) =>
        http.json<{ templates: TemplateSummary[] }>("GET", "/superuser/email/templates", { query: { lang } }),
      get: (key: TemplateKey, lang: Lang) =>
        http.json<TemplateDetail>("GET", `/superuser/email/templates/${key}`, { query: { lang } }),
      save: (key: TemplateKey, lang: Lang, body: TemplateSaveRequest) =>
        http.json<TemplateDetail>("PUT", `/superuser/email/templates/${key}`, { query: { lang }, body }),
      preview: (key: TemplateKey, lang: Lang) =>
        http.text("GET", `/superuser/email/templates/${key}/preview`, { query: { lang } }),
      test: (key: TemplateKey, lang: Lang, to: string) =>
        http.json<{ outbox_id: string }>("POST", `/superuser/email/templates/${key}/test`, {
          query: { lang },
          body: { to },
        }),
    },
    holds: {
      list: () => http.json<{ holds: Hold[] }>("GET", "/superuser/email/holds"),
      release: (holdId: string) =>
        http.json<{ released: number }>("POST", `/superuser/email/holds/${id(holdId)}/release`),
    },

    /* 45.8 Campaigns */
    campaigns: {
      list: () => http.json<{ campaigns: Campaign[] }>("GET", "/superuser/email/campaigns"),
      create: (body: CampaignCreateRequest) =>
        http.json<Campaign>("POST", "/superuser/email/campaigns", { body }),
      act: (campaignId: string, action: CampaignAction) =>
        http.json<Campaign>("POST", `/superuser/email/campaigns/${id(campaignId)}/${action}`),
    },

    /* 45.9-45.10 Attendee record */
    attendees: {
      get: (accountId: string) => http.json<AttendeeRecord>("GET", `/superuser/attendees/${id(accountId)}`),
      correct: (accountId: string, body: AttendeeCorrection) =>
        http.json<AttendeeRecord>("PATCH", `/superuser/attendees/${id(accountId)}`, { body }),
      saveProfile: (accountId: string, profile: ProfileInput) =>
        http.json<AttendeeRecord>("PUT", `/superuser/attendees/${id(accountId)}/profile`, { body: { profile } }),
      resendVerification: (accountId: string) =>
        http.json<{ expires_at: string }>("POST", `/superuser/attendees/${id(accountId)}/resend-verification`),
      book: (accountId: string, sessionId: string) =>
        http.json<AttendeeRecord>("POST", `/superuser/attendees/${id(accountId)}/rsvps`, {
          body: { session_id: sessionId },
        }),
      release: (accountId: string, sessionId: string) =>
        http.json<AttendeeRecord>("DELETE", `/superuser/attendees/${id(accountId)}/rsvps/${id(sessionId)}`),
      resendPack: (accountId: string) =>
        http.json<{ outcome: string }>("POST", `/superuser/attendees/${id(accountId)}/resend-pack`),
      undoCheckIn: (accountId: string) =>
        http.json<AttendeeRecord>("DELETE", `/superuser/attendees/${id(accountId)}/check-in`),
      remove: (accountId: string) => http.void("DELETE", `/superuser/attendees/${id(accountId)}`),
    },
  };
}

export type SuperuserApi = ReturnType<typeof createSuperuserApi>;
